<template>
  <div
    class="w-auto h-auto collapse navbar-collapse max-height-vh-100 h-100"
    id="sidenav-collapse-main"
  >
    <ul class="navbar-nav">
      <template v-for="group in groupedMenus" :key="group.name">
        <li class="nav-item mt-3">
          <h6 class="ps-4 ms-2 text-uppercase text-xs font-weight-bolder opacity-6 sidenav-section-label">
            {{ group.name }}
          </h6>
        </li>

        <li
          v-for="item in group.items"
          :key="item.menuId ?? item.menuKey ?? item.routePath ?? item.menuName"
          class="nav-item"
        >
          <sidenav-collapse
            :navText="item.menuName"
            :to="item.to"
            :faIcon="item.icon"
            :children="item.children || []"
          />
        </li>
      </template>
    </ul>
  </div>
</template>

<script>
import SidenavCollapse from "./SidenavCollapse.vue";
import { buildSidenavMenus } from "@/utils/menuHierarchy";
import {
  loadAndStoreMenuAccess,
  resolveMenuRouteTarget,
} from "@/utils/menuAccess";

export default {
  name: "SidenavList",
  props: {
    cardBg: String,
  },
  data() {
    return {
      menuItems: [],
    };
  },
  computed: {
    /**
     * Static fallback / authored menu definition used when the backend menu API
     * does not return anything. The "System Setting" entry exposes children
     * so it renders as an expandable group in the hamburger sidenav.
     */
    fallbackMenus() {
      return [
        { menuName: "Dashboard", routeName: "Dashboard", sectionName: "Main", icon: "fas fa-chart-pie", sortOrder: 10, to: { name: "Dashboard" } },
        { menuName: "My Tasks", routeName: "My Tasks", sectionName: "Main", icon: "fas fa-clipboard-list", sortOrder: 20, to: { name: "My Tasks" } },
        {
          menuName: "Project Management",
          routeName: "Project Overview",
          sectionName: "Main",
          icon: "fas fa-project-diagram",
          sortOrder: 31,
          to: { name: "Project Overview" },
          children: [
            { menuKey: "project_overview_all", menuName: "Overview", routeName: "Project Overview All", routePath: "/project-overview/all", icon: "fas fa-th-large", to: { name: "Project Overview All" } },
            { menuKey: "project_overview_my", menuName: "My Projects", routeName: "Project Overview My", routePath: "/project-overview/my", icon: "fas fa-user-clock", to: { name: "Project Overview My" } },
            { menuKey: "project_management", menuName: "Management", routeName: "Tables", routePath: "/tables", icon: "fas fa-folder-open", to: { name: "Tables" } },
          ],
        },
        { menuName: "Automation", routeName: "Automation Tracking", sectionName: "Main", icon: "fas fa-robot", sortOrder: 32, to: { name: "Automation Tracking" } },
        {
          menuName: "Plan Management",
          routeName: "Plan Management Team",
          sectionName: "Main",
          icon: "fas fa-users",
          sortOrder: 40,
          to: { name: "Plan Management Team" },
          children: [
            { menuKey: "plan_team_all", menuName: "Overview", routeName: "Plan Team All", routePath: "/test-plan-team/all", icon: "fas fa-layer-group", to: { name: "Plan Team All" } },
            { menuKey: "plan_team_a", menuName: "Team A", routeName: "Plan Team A", routePath: "/test-plan-team/team-a", icon: "fas fa-user-friends", to: { name: "Plan Team A" } },
            { menuKey: "plan_team_b", menuName: "Team B", routeName: "Plan Team B", routePath: "/test-plan-team/team-b", icon: "fas fa-user-friends", to: { name: "Plan Team B" } },
            { menuKey: "plan_team_c", menuName: "Team C", routeName: "Plan Team C", routePath: "/test-plan-team/team-c", icon: "fas fa-user-friends", to: { name: "Plan Team C" } },
          ],
        },
        { menuName: "QA Management", routeName: "QA Management", sectionName: "Main", icon: "fas fa-clipboard-check", sortOrder: 42, to: { name: "QA Management" },
          children: [
            { menuKey: "qa_test_case", menuName: "Test Case", routeName: "QA Test Case", routePath: "/qa-management/test-case", icon: "fas fa-list-check", to: { name: "QA Test Case" } },
            { menuKey: "qa_test_run", menuName: "Test Run", routeName: "QA Test Run", routePath: "/qa-management/test-run", icon: "fas fa-play-circle", to: { name: "QA Test Run" } },
            { menuKey: "qa_test_report", menuName: "Test Report", routeName: "QA Test Report", routePath: "/qa-management/test-report", icon: "fas fa-file-alt", to: { name: "QA Test Report" } },
          ],
        },
        { menuName: "Profile", routeName: "Profile", sectionName: "Account", icon: "fas fa-user-circle", sortOrder: 50, to: { name: "Profile" } },
        { menuName: "User Setting", routeName: "User Setting", sectionName: "Account", icon: "fas fa-cog", sortOrder: 60, to: { name: "User Setting" } },
        { menuName: "Role Permission", routeName: "Role Permission", sectionName: "System", icon: "fas fa-user-shield", sortOrder: 70, to: { name: "Role Permission" } },
        {
          menuName: "System Setting",
          routeName: "System Setting",
          sectionName: "System",
          icon: "fas fa-sliders-h",
          sortOrder: 80,
          to: { name: "System Setting" },
        },
        { menuName: "Team Management", routeName: "Management", sectionName: "Main", icon: "fas fa-users-cog", sortOrder: 75, to: { name: "Management" } },
      ];
    },
    effectiveMenus() {
      if (this.menuItems.length) return this.menuItems;
      const userId = this.$store?.state?.user?.userId;
      if (userId) {
        const rows = this.$store.state.menuAccess || [];
        if (rows.length) {
          return buildSidenavMenus(rows, (r) => this.resolveRouteTarget(r));
        }
        return [];
      }
      return this.fallbackMenus;
    },
    groupedMenus() {
      const sectionOrder = ["Main", "Account", "System"];
      const groups = sectionOrder
        .map((name) => ({
          name,
          items: this.effectiveMenus.filter((m) => (m.sectionName || "Main") === name),
        }))
        .filter((g) => g.items.length > 0);
      return groups;
    },
  },
  async created() {
    await this.loadMenus();
  },
  watch: {
    "$store.state.user.userId"(userId) {
      if (userId) this.loadMenus();
    },
    "$store.state.menuAccess": {
      handler(rows) {
        if (!Array.isArray(rows) || !rows.length) return;
        this.menuItems = buildSidenavMenus(rows, (r) =>
          this.resolveRouteTarget(r),
        );
      },
      deep: true,
    },
  },
  methods: {
    async loadMenus() {
      const userId = this.$store?.state?.user?.userId;
      if (!userId) return;
      try {
        const rows = await loadAndStoreMenuAccess(this.$store, userId);
        this.menuItems = buildSidenavMenus(rows || [], (r) =>
          this.resolveRouteTarget(r),
        );
      } catch (e) {
        console.error("loadMenus error:", e);
      }
    },
    resolveRouteTarget(menuRow) {
      return resolveMenuRouteTarget(menuRow, this.$router);
    },
  },
  components: {
    SidenavCollapse,
  },
};
</script>
